import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { WhatsAppIcon } from "@/components/Logo";
import { WHATSAPP_LINK } from "@/site";
import { IMGS } from "@/data";
import { useLang } from "@/i18n";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const ySec = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden pt-[210px] md:pt-[240px]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-overline"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-8 text-xs font-semibold uppercase tracking-[0.28em] text-clay"
          >
            {t.hero.overline}
          </motion.p>
          <h1
            data-testid="hero-heading"
            className="font-serif text-5xl leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.15}>{t.hero.h1a}</MaskedLine>
            <MaskedLine delay={0.32}>
              <em className="italic text-clay">{t.hero.h1b}</em>
            </MaskedLine>
          </h1>
          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-ink/60 md:text-lg"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#quote"
              data-testid="hero-quote-button"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-bone transition-colors duration-300 hover:bg-clay"
            >
              {t.hero.cta1}
              <ArrowDown className="h-4 w-4" />
            </a>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-bone"
            >
              <WhatsAppIcon className="h-4 w-4" />
              {t.hero.cta2}
            </a>
          </motion.div>
          <motion.dl
            data-testid="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-16 grid grid-cols-2 gap-8 border-t border-ink/10 pt-8 sm:grid-cols-4"
          >
            {t.hero.stats.map((s) => (
              <div key={s.l}>
                <dt className="sr-only">{s.l}</dt>
                <dd className="font-serif text-3xl text-ink">{s.v}</dd>
                <dd className="mt-1 text-xs uppercase tracking-[0.16em] text-ink/50">{s.l}</dd>
              </div>
            ))}
          </motion.dl>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: yMain }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative ml-auto aspect-[4/5] w-full max-w-md overflow-hidden bg-sand shadow-[0_24px_60px_rgba(0,0,0,0.10)]"
          >
            <img
              src="/images/site-store.webp"
              alt="A retailer browsing racks of finished garments produced by HHB Clothing Co"
              data-testid="hero-image-main"
              className="h-full w-full object-cover"
            />
          </motion.div>
          <motion.div
            style={{ y: ySec }}
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="absolute -bottom-10 -start-4 w-40 overflow-hidden border-4 border-bone bg-sand shadow-[0_16px_40px_rgba(0,0,0,0.14)] sm:w-52 md:-start-10"
          >
            <img
              src="/images/site-kaftan.jpg"
              alt="Emerald kaftan on a brass rail, produced by HHB Clothing Co"
              data-testid="hero-image-secondary"
              className="aspect-[3/4] w-full object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            data-testid="hero-badge"
            className="absolute -end-2 top-8 hidden bg-ink px-5 py-4 text-bone md:block"
          >
            <p className="font-serif text-2xl">8</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-bone/60">stages, one roof</p>
          </motion.div>
        </div>
      </div>
      <div className="h-20 md:h-28" />
    </section>
  );
};

export default Hero;
